import { useRef, useState } from "react";
import { api, formatApiErrorDetail, parseNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Upload, Loader2, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

const fmtEur = (n) =>
  "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function ReceiptUpload({ requestId, requestedAmount, onDone }) {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [amount, setAmount] = useState("");
  const [liters, setLiters] = useState("");
  const [loading, setLoading] = useState(false);

  const openDialog = () => {
    setFile(null);
    setAmount(requestedAmount != null ? String(requestedAmount) : "");
    setLiters("");
    setOpen(true);
  };

  const submit = async () => {
    if (!file) {
      toast.error("Seleziona la foto dello scontrino");
      return;
    }
    const amt = parseNum(amount);
    if (!amt || amt <= 0) {
      toast.error("Inserisci l'importo effettivo del rifornimento");
      return;
    }
    const lit = parseNum(liters);
    if (!lit || lit <= 0) {
      toast.error("Inserisci i litri di carburante prelevati");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/receipt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.put(`/requests/${requestId}/receipt`, { receipt_path: data.path, actual_amount: amt, liters: lit });
      toast.success("Scontrino caricato");
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        onClick={openDialog}
        data-testid="upload-receipt-btn"
        className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]"
      >
        <Upload className="mr-1.5 h-4 w-4" /> Carica scontrino
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="receipt-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Carica scontrino</DialogTitle>
            <DialogDescription>
              Allega la foto dello scontrino e indica l'importo effettivo del rifornimento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
              Importo autorizzato: <b className="text-slate-900">{fmtEur(requestedAmount)}</b>
            </div>
            <div>
              <Label htmlFor="actual-amount">Importo effettivo del rifornimento (€)</Label>
              <Input
                id="actual-amount"
                type="text"
                inputMode="decimal"
                min="0"
                step="0.01"
                data-testid="receipt-actual-amount-input"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1.5"
                placeholder="es. 48.30"
              />
            </div>
            <div>
              <Label htmlFor="actual-liters">Litri prelevati</Label>
              <Input
                id="actual-liters"
                type="text"
                inputMode="decimal"
                min="0"
                step="0.01"
                data-testid="receipt-liters-input"
                value={liters}
                onChange={(e) => setLiters(e.target.value)}
                className="mt-1.5"
                placeholder="es. 17.26"
              />
              <p className="mt-1 text-xs text-slate-400">Indica i litri esatti riportati sullo scontrino (obbligatorio).</p>
            </div>
            <div>
              <input
                ref={inputRef}
                type="file"
                accept="image/*,.pdf"
                capture="environment"
                className="hidden"
                data-testid="receipt-upload-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <Button
                variant="outline"
                onClick={() => inputRef.current?.click()}
                data-testid="receipt-pick-file-btn"
                className="w-full rounded-md"
              >
                {file ? <FileCheck2 className="mr-2 h-4 w-4 text-emerald-600" /> : <Upload className="mr-2 h-4 w-4" />}
                {file ? file.name : "Scegli foto / PDF"}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} disabled={loading} data-testid="confirm-receipt-btn" className="bg-[#002FA7] text-white hover:bg-[#00227A]">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
